// Shop money formats vary (59.00 vs 59,00 vs 1.234,56) - this normalizes
// any of them to a plain JS number instead of assuming a period decimal.
function rizonParseMoney(str) {
  if (!str) return 0;
  const stripped = String(str).trim().replace(/[^0-9.,-]/g, '');
  const lastComma = stripped.lastIndexOf(',');
  const lastDot = stripped.lastIndexOf('.');
  if (lastComma === -1 && lastDot === -1) return Number(stripped) || 0;
  const decimalSep = lastComma > lastDot ? ',' : '.';
  const thousandsSep = decimalSep === ',' ? '.' : ',';
  const normalized = stripped.split(thousandsSep).join('').replace(decimalSep, '.');
  return Number(normalized) || 0;
}

class RizonPriceSlider extends HTMLElement {
  connectedCallback() {
    this.min = rizonParseMoney(this.dataset.min) || 0;
    this.max = rizonParseMoney(this.dataset.max) || 100;
    this.inputMin = document.getElementById(this.dataset.inputMin);
    this.inputMax = document.getElementById(this.dataset.inputMax);
    this.track = this.querySelector('.rizon-filters-dialog__price-track');
    this.fill = this.querySelector('[data-price-fill]');
    this.thumbMin = this.querySelector('[data-price-thumb="min"]');
    this.thumbMax = this.querySelector('[data-price-thumb="max"]');

    this.valueMin = this.clamp(rizonParseMoney(this.inputMin?.value) || this.min);
    this.valueMax = this.clamp(rizonParseMoney(this.inputMax?.value) || this.max);

    this.render();

    this.thumbMin.addEventListener('pointerdown', (event) => this.startDrag(event, 'min'));
    this.thumbMax.addEventListener('pointerdown', (event) => this.startDrag(event, 'max'));
    this.thumbMin.addEventListener('keydown', (event) => this.onKeyDown(event, 'min'));
    this.thumbMax.addEventListener('keydown', (event) => this.onKeyDown(event, 'max'));

    this.inputMin?.addEventListener('change', () => this.onInputChange('min'));
    this.inputMax?.addEventListener('change', () => this.onInputChange('max'));
  }

  clamp(value) {
    return Math.min(this.max, Math.max(this.min, value));
  }

  percentFor(value) {
    if (this.max === this.min) return 0;
    return (value - this.min) / (this.max - this.min);
  }

  // Positions the thumb's LEFT EDGE (not center) so the 14px-wide thumb
  // never extends past the track's own box - at ratio 0 the edge sits at
  // 0, at ratio 1 the edge sits at (100% - 14px) so the thumb's far edge
  // lands exactly on 100%. Previously this used raw `left: ratio%` with a
  // -7px margin to visually center the thumb on the line, which let it
  // poke 7px outside the track at both ends and get clipped by the
  // accordion content's overflow: hidden.
  edgeExpr(ratio) {
    return `(100% - ${RizonPriceSlider.THUMB_SIZE}px) * ${ratio}`;
  }

  centerExpr(ratio) {
    return `${this.edgeExpr(ratio)} + ${RizonPriceSlider.THUMB_RADIUS}px`;
  }

  render() {
    const minRatio = this.percentFor(this.valueMin);
    const maxRatio = this.percentFor(this.valueMax);
    this.thumbMin.style.left = `calc(${this.edgeExpr(minRatio)})`;
    this.thumbMax.style.left = `calc(${this.edgeExpr(maxRatio)})`;
    const minCenter = this.centerExpr(minRatio);
    const maxCenter = this.centerExpr(maxRatio);
    this.fill.style.left = `calc(${minCenter})`;
    this.fill.style.width = `calc((${maxCenter}) - (${minCenter}))`;
    this.thumbMin.setAttribute('aria-valuenow', Math.round(this.valueMin));
    this.thumbMax.setAttribute('aria-valuenow', Math.round(this.valueMax));
  }

  startDrag(event, which) {
    event.preventDefault();
    const thumb = which === 'min' ? this.thumbMin : this.thumbMax;
    thumb.setPointerCapture(event.pointerId);

    const onMove = (moveEvent) => {
      const rect = this.track.getBoundingClientRect();
      const usableWidth = rect.width - RizonPriceSlider.THUMB_SIZE;
      const ratio = (moveEvent.clientX - rect.left - RizonPriceSlider.THUMB_RADIUS) / usableWidth;
      this.setValue(which, this.min + ratio * (this.max - this.min));
    };

    const onUp = () => {
      thumb.releasePointerCapture(event.pointerId);
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      this.commit(which);
    };

    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  }

  onKeyDown(event, which) {
    const step = (this.max - this.min) / 100 || 1;
    let delta = 0;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') delta = -step;
    else if (event.key === 'ArrowRight' || event.key === 'ArrowUp') delta = step;
    else return;
    event.preventDefault();
    const current = which === 'min' ? this.valueMin : this.valueMax;
    this.setValue(which, current + delta);
    this.commit(which);
  }

  onInputChange(which) {
    const input = which === 'min' ? this.inputMin : this.inputMax;
    this.setValue(which, rizonParseMoney(input.value), false);
  }

  setValue(which, rawValue, updateInput = true) {
    let value = this.clamp(Math.round(rawValue));
    if (which === 'min') {
      value = Math.min(value, this.valueMax);
      this.valueMin = value;
    } else {
      value = Math.max(value, this.valueMin);
      this.valueMax = value;
    }
    this.render();
    if (updateInput) {
      const input = which === 'min' ? this.inputMin : this.inputMax;
      if (input) input.value = value;
    }
  }

  commit(which) {
    const input = which === 'min' ? this.inputMin : this.inputMax;
    if (!input) return;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // Called by rizon-filters-dialog.js when "Clear all" (or this group's own
  // "Remove") is clicked - blank inputs (not the literal min/max numbers)
  // match how Dawn renders an unfiltered price field, so submitting the
  // form afterward doesn't send an explicit-but-redundant gte=0/lte=max.
  reset() {
    this.valueMin = this.min;
    this.valueMax = this.max;
    this.render();
    if (this.inputMin) this.inputMin.value = '';
    if (this.inputMax) this.inputMax.value = '';
  }

  // Called by rizon-filters-dialog.js when closing the dialog without
  // Apply - the inputs get reverted to their pre-open values directly, so
  // this just re-reads them and re-renders the thumbs/fill to match
  // (same value resolution as the initial connectedCallback read).
  syncFromInputs() {
    this.valueMin = this.clamp(rizonParseMoney(this.inputMin?.value) || this.min);
    this.valueMax = this.clamp(rizonParseMoney(this.inputMax?.value) || this.max);
    this.render();
  }
}

RizonPriceSlider.THUMB_SIZE = 14;
RizonPriceSlider.THUMB_RADIUS = 7;

customElements.define('rizon-price-slider', RizonPriceSlider);
